import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStores, useUser } from '../../hooks';
import { Icon, Icons, Typography } from '../../stories/atoms';
import { ColumnFlexContainer, ContainerV2, RowFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { homeTranslations } from '../../translations';
import { isFailureResponse } from '../../utils';
import {
  ContentWrapper,
  HeaderContainer,
  InputContainer,
  StyledInput,
  StyledSubmitButton,
} from './Chatbot.styles';
import { MessageItem } from './messageItem/MessageItem';

type Message = {
  isSender: boolean;
  text: string;
  prompts?: string[];
};

export type ChatbotProps = {
  isOpen: boolean;
  closeChatbot: () => void;
};

const INIT_MESSAGE = 'hi';

export const Chatbot: FC<ChatbotProps> = ({ isOpen, closeChatbot }) => {
  const { t } = useTranslation();
  const { slider } = homeTranslations;
  const { fetchChatBotData } = useStores();
  const {
    get: {
      profile: { avatar },
    },
  } = useUser();

  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isError, setIsError] = useState(false);
  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);

  const onMessageReceive = (message: string, prompts: string[]) => {
    setMessages((prevMessages) => [...prevMessages, { isSender: false, text: message, prompts }]);
  };

  const sendMessage = async (message: string, isInit: boolean = false) => {
    setIsSendingMessage(true);

    const response = await fetchChatBotData(message);

    if (isFailureResponse(response)) {
      setIsSendingMessage(false);
      setIsError(true);
      return;
    }

    setIsSendingMessage(false);
    setIsError(false);

    const {
      data: { answer, prompts },
    } = response;

    if (isInit) setMessages([]);

    onMessageReceive(answer, prompts);
  };

  const initChat = () => {
    setMessages([]);
    sendMessage(INIT_MESSAGE, true);
  };

  const handlePromptSelect = (prompt: string) => {
    setMessages((prevMessages) => [...prevMessages, { isSender: true, text: prompt }]);
    sendMessage(prompt);
  };

  const handleSubmit = () => {
    if (inputText === '') return;

    setMessages((prevMessages) => [...prevMessages, { isSender: true, text: inputText }]);
    sendMessage(inputText);
    setInputText('');
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code != 'Enter') return;
    handleSubmit();
  };

  useEffect(() => {
    if (isOpen) initChat();
  }, [isOpen]);

  useEffect(() => {
    if (!messageContainerRef.current || messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    if (!lastMessage.isSender) return;

    messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
  }, [messages]);

  return (
    <ContentWrapper data-testid="wrapper" isOpen={isOpen}>
      <HeaderContainer alignItems="center" height={[15]} padding={[0, 5]} data-testid="header-container">
        <Icon icon={Icons.CHATBOT} />

        <ContainerV2 margin={[0, 0, 0, 5]} flex={1} data-testid="header-title-container">
          <Typography type="body2">Chat Box</Typography>
        </ContainerV2>

        <ContainerV2 margin={[0, 0, 0, 5]} cursor="pointer" onClick={initChat} data-testid="header-refresh-button">
          <Icon icon={Icons.REFRESH} size="small" />
        </ContainerV2>

        <ContainerV2
          margin={[0, 0, 0, 5]}
          cursor="pointer"
          onClick={closeChatbot}
          data-testid="header-close-button"
        >
          <Icon icon={Icons.CLOSE} size="small" />
        </ContainerV2>
      </HeaderContainer>

      {isError && (
        <RowFlexContainer
          textAlign="center"
          padding={[0, 10]}
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <Typography type="button">
            {t(slider.chatbotFailureText.key, slider.chatbotFailureText.defaultValue)}
          </Typography>
        </RowFlexContainer>
      )}

      {!isError && (
        <ColumnFlexContainer
          flex={1}
          overflow="scroll"
          padding={[4]}
          ref={messageContainerRef}
          data-testid="message-container"
        >
          <ColumnFlexContainer>
            {messages.map((message, index) => {
              const { isSender, text, prompts } = message;

              return (
                <div key={index}>
                  <MessageItem
                    isSender={isSender}
                    text={text}
                    prompts={prompts}
                    avatar={avatar?.url ? `${avatar?.url}${process.env.REACT_APP_CDN_TOKEN}` : undefined}
                    onPromptSelect={handlePromptSelect}
                  />
                </div>
              );
            })}
            {isSendingMessage && <Icon icon={Icons.CHATBUBBLE} size="small" />}
          </ColumnFlexContainer>
        </ColumnFlexContainer>
      )}

      <InputContainer position="relative" width="100%" height={[18]} padding={[3]} data-testid="input-container">
        <StyledInput
          placeholder={t(slider.chatBotPlaceholder.key, slider.chatBotPlaceholder.defaultValue)}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleOnKeyDown}
        />

        <StyledSubmitButton data-testid="submit-button" onClick={() => handleSubmit()}>
          {inputText === '' && <Icon icon={Icons.EDIT} size="small" />}

          {inputText !== '' && <Icon icon={Icons.ARROW} size="small" />}
        </StyledSubmitButton>
      </InputContainer>
    </ContentWrapper>
  );
};
